import qs from 'qs';

import { APIService } from '../../api/apiService';
import type {
  GetSharePointFoldersParams,
  GetSharePointSitesParams,
  SharePointCertificate,
  SharePointConnectionTest,
  SharePointEditSession,
  SharePointFolderLevel,
  SharePointSignInConfig,
  SharePointSiteList,
  SharePointStatus,
  SharePointViewerToken,
} from '../../interfaces/SharePointApiService';

const BASE_URL = '/sharepoint';

/**
 * SharePoint integration (SharePointGrant) routes: the configuration ones, which are admin-only, plus
 * {@link SharePointService.getViewerToken} — the one route any user hits when opening a file.
 *
 * Every method below performs live Microsoft Graph calls against the customer's tenant and shares a
 * 60 req/min budget with the AI endpoints, so callers must never poll, never fire on keystroke and
 * never prefetch the folder tree: load one level per node expansion and cache the sites list for the
 * duration of the wizard.
 */
export class SharePointService extends APIService {
  /**
   * @desc Verify the STORED credentials against Microsoft Graph. Read-only: it enumerates the
   * tenant's sites and touches no file, but it does rewrite the verification stamps on the setting,
   * so re-read the setting (or the status) afterwards.
   * @return A refused verification is a `200` with `ok: false`, not an HTTP error.
   */
  testConnection(): Promise<SharePointConnectionTest> {
    return this.request(`${BASE_URL}/connection/test`, 'POST');
  }

  /**
   * @desc List one page of the sites the stored credentials can see.
   * @param params `offset`/`count` window (defaults 0/50, count clamped to 1..200) and a `search`
   * substring applied to the whole tenant before the window.
   * @return The page plus `total` across the whole tenant. Because the tenant is enumerated
   * server-side in full before the window is applied, every page costs the same Graph call: prefer
   * `search` over deep paging and debounce the search box.
   */
  getSites(params: GetSharePointSitesParams = {}): Promise<SharePointSiteList> {
    const query = qs.stringify(params, { skipNulls: true });
    return this.request(`${BASE_URL}/sites${query ? `?${query}` : ''}`, 'GET');
  }

  /**
   * @desc Load ONE level of the perimeter picker.
   * @param siteId Graph site id from {@link getSites}; encoded here because it contains commas.
   * @param params Omit `driveId` to list the site's document libraries; pass it (with an optional
   * `path`) to list the folders inside that library. `offset`/`count` window whichever of the two
   * this level returns.
   * @return The requested level. Only folders are returned — files are never selectable. A
   * `truncated: true` level sits above the 2000-folder ceiling: `total` is then a floor and some
   * folder will never appear on any page.
   */
  getFolders(
    siteId: string,
    params: GetSharePointFoldersParams = {},
  ): Promise<SharePointFolderLevel> {
    const query = qs.stringify(params, { skipNulls: true });
    return this.request(
      `${BASE_URL}/sites/${encodeURIComponent(siteId)}/folders${query ? `?${query}` : ''}`,
      'GET',
    );
  }

  /**
   * @desc List the RemoteGrant certificates the perimeter can be encrypted to.
   * @return The company's own certificates, with `selected` marking the stored one. No key material,
   * no expiry and no revocation state — RemoteGrant exposes none of those.
   *
   * Unlike the other methods here this one talks to RemoteGrant, not to Microsoft Graph: it works
   * before the SharePoint connection is verified and does not depend on the perimeter. It still
   * shares the same 60 req/min budget, so call it on demand (when the picker is opened or refreshed)
   * and never on page mount nor in a poll.
   */
  getCertificates(): Promise<SharePointCertificate[]> {
    return this.request(`${BASE_URL}/certificates`, 'GET');
  }

  /**
   * @desc Read the scan state: whether anything is being encrypted and, if not, why not.
   * @return Always `200` unless the licence flag is off (`403 SHAREPOINT_NOT_AVAILABLE`); an
   * unusable configuration is reported inside the body as `configurationError`/`perimeterError`.
   */
  getStatus(): Promise<SharePointStatus> {
    return this.request(`${BASE_URL}/status`, 'GET');
  }

  /**
   * @desc Read the per-company MSAL configuration: the customer's tenant and the PUBLIC (SPA) app
   * registration the browser signs into to obtain a delegated Graph token. Available to any
   * authenticated user, and the first call of both the viewer and the editor.
   * @return `{ tenantId, spaClientId }`. Never a secret: both values travel in every OAuth authorize
   * URL. No Graph call behind it, so this one is NOT in the `expensive` bucket — but it is still per
   * company, so cache it for the tab instead of re-fetching per file.
   *
   * Failure worth handling apart: `404 SHAREPOINT_SPA_APP_NOT_CONFIGURED` means the administrator has
   * not configured the SPA app registration; no sign-in is possible and no file can be opened.
   */
  getSignInConfig(): Promise<SharePointSignInConfig> {
    return this.request(`${BASE_URL}/signin/config`, 'GET');
  }

  /**
   * @desc Obtain the token that lets the PDF-processing service open ONE encrypted SharePoint file.
   * The only route here that is not admin-only: any user opening a file calls it.
   * @param driveId Graph drive id of the item, from the Graph metadata call.
   * @param itemId Graph `driveItem.id`, unique within that drive.
   * @param graphToken The user's DELEGATED Graph access token. The API reads the file's header with
   * it, and that read is what enforces the SharePoint permissions: no permission, no key.
   * @return A token valid for ~2 minutes carrying the file's AES key, encrypted with the master
   * symmetric key. Hand it straight to the PDF-processing service; never store it, never log it.
   *
   * Failure worth handling apart: `404 SHAREPOINT_ITEM_NOT_PROTECTED` means Filegrant never
   * encrypted that item, so it opens as it is and no token is needed — not an error for the viewer.
   */
  getViewerToken(
    driveId: string,
    itemId: string,
    graphToken: string,
  ): Promise<SharePointViewerToken> {
    return this.request(
      `${BASE_URL}/fileviewer/token`,
      'POST',
      { driveId, itemId },
      { 'X-Graph-Token': graphToken },
    );
  }

  /**
   * @desc Open an editing session on ONE encrypted SharePoint file, so an office editor can open it
   * and save it back still encrypted. Like {@link getViewerToken}, available to any user.
   * @param driveId Graph drive id of the item.
   * @param itemId Graph `driveItem.id`, unique within that drive.
   * @param graphToken The user's DELEGATED Graph access token. The API reads the item and its header
   * with it — that read is the permission check — and asks SharePoint whether the user may WRITE, so
   * this token needs the write scope (see `sharePointGraphWriteRequest`). A read-only token still
   * works: the session comes back with `canWrite: false`.
   * @return The launch data for the editor. Three Graph calls happen behind this one request, so call
   * it once per document opened and never speculatively.
   *
   * Failures worth handling apart: `404 SHAREPOINT_ITEM_NOT_PROTECTED` (Filegrant never encrypted the
   * item — it opens in SharePoint's own Office, nothing to do here), `400
   * SHAREPOINT_EDIT_UNSUPPORTED_TYPE` (not an editable format: stay in the viewer) and `413
   * SHAREPOINT_EDIT_FILE_TOO_LARGE`.
   */
  openEditSession(
    driveId: string,
    itemId: string,
    graphToken: string,
  ): Promise<SharePointEditSession> {
    return this.request(
      `${BASE_URL}/fileeditor/session`,
      'POST',
      { driveId, itemId },
      { 'X-Graph-Token': graphToken },
    );
  }
}
